import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { generateContinuityObservations, type ContinuityObservation } from "@/lib/continuity";
import { fetchProgressionContinuityLines } from "@/lib/progression";

interface Props {
  rotateMs?: number;
}

/**
 * Ambient Memory Strip — quietly rotating cross-session observations.
 * Restrained, archival, low-opacity. Surfaces only when 2+ sessions exist
 * and recurring patterns have been detected.
 */
const AmbientMemoryStrip = ({ rotateMs = 12000 }: Props) => {
  const { user } = useAuth();
  const [observations, setObservations] = useState<ContinuityObservation[]>([]);
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    Promise.all([
      generateContinuityObservations(user.id),
      fetchProgressionContinuityLines(user.id),
    ])
      .then(([obs, progLines]) => {
        if (cancelled) return;
        const progressionObs: ContinuityObservation[] = progLines.map((text) => ({
          text,
          kind: "escalation",
        }));
        // Interleave so progression memory surfaces alongside continuity
        const merged: ContinuityObservation[] = [];
        const max = Math.max(obs.length, progressionObs.length);
        for (let i = 0; i < max; i++) {
          if (progressionObs[i]) merged.push(progressionObs[i]);
          if (obs[i]) merged.push(obs[i]);
        }
        setObservations(merged);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user]);

  useEffect(() => {
    if (observations.length < 2) return;
    const interval = window.setInterval(() => {
      setVisible(false);
      window.setTimeout(() => {
        setIndex((i) => (i + 1) % observations.length);
        setVisible(true);
      }, 900);
    }, rotateMs);
    return () => window.clearInterval(interval);
  }, [observations, rotateMs]);

  if (!user || observations.length === 0) return null;

  const current = observations[index];

  return (
    <section
      aria-label="Observed continuity"
      className="mx-auto max-w-xl text-center select-none"
      style={{ animation: "ambientBreath 8s ease-in-out infinite" }}
    >
      <p className="text-[9px] font-mono tracking-[0.42em] uppercase text-muted-foreground/40 mb-3">
        Observed Continuity
      </p>
      <p
        className="text-[13px] md:text-sm text-foreground/55 italic font-light leading-relaxed transition-opacity duration-[900ms] ease-out"
        style={{ opacity: visible ? 0.78 : 0 }}
      >
        {current.text}
      </p>
      <div className="mx-auto mt-4 h-px w-16 bg-gradient-to-r from-transparent via-cyan/15 to-transparent" />

      <style>{`
        @keyframes ambientBreath {
          0%, 100% { opacity: 0.78; }
          50%      { opacity: 1; }
        }
      `}</style>
    </section>
  );
};

export default AmbientMemoryStrip;
